import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Inbox, Search, SearchX } from 'lucide-react';
import { getSectionBySlug, getErrorCode, getCategoryBySlug } from '@/services/api';
import { Breadcrumbs, ArticleRow } from '@/components';
import { ErrorState, EmptyState, LoadingSpinner } from '@airclou/ui';

/**
 * Past this many articles a section is treated as "long": rows go title-only
 * and a filter box appears above them. Below it the list is short enough to
 * read whole, so the richer rows are worth their height.
 */
const LONG_SECTION = 20;

/**
 * Case- and accent-insensitive comparison form, so a visitor typing "reglement"
 * still finds "règlement". Portals are per-brand and multi-locale, and nobody
 * reaches for the accent keys when they are hunting for an answer.
 */
function fold(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/** Keep an echoed search term to one line — people paste whole error messages in. */
function truncateNeedle(value: string): string {
  return value.length > 60 ? `${value.slice(0, 60)}...` : value;
}

export function SectionPage() {
  const { slug, sectionSlug, categorySlug } = useParams<{ slug?: string; sectionSlug?: string; categorySlug?: string }>();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);

  // Support both old (/sections/:slug) and new (/:categorySlug/:sectionSlug) formats
  const effectiveSlug = sectionSlug || slug;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['section', effectiveSlug, categorySlug],
    queryFn: () => getSectionBySlug(effectiveSlug!, categorySlug),
    enabled: !!effectiveSlug,
  });

  const sectionCategorySlug = data?.section?.category?.slug;
  const effectiveCategorySlug = categorySlug || sectionCategorySlug;

  const {
    data: categoryData,
  } = useQuery({
    queryKey: ['category', effectiveCategorySlug],
    queryFn: () => getCategoryBySlug(effectiveCategorySlug!),
    enabled: !!effectiveCategorySlug && !data?.section?.category?.name,
    staleTime: 5 * 60 * 1000,
  });

  // Redirect legacy URLs to clean format
  useEffect(() => {
    if (!effectiveSlug || !sectionCategorySlug) return;
    if (sectionSlug) return; // Already using new format

    navigate(`/${sectionCategorySlug}/${effectiveSlug}`, { replace: true });
  }, [sectionSlug, navigate, sectionCategorySlug, effectiveSlug]);

  // A different section means a different list — carrying the old needle over
  // would greet the visitor with "No matching articles" on a section that is
  // in fact full of them. The route element is reused across sections, so
  // nothing resets this for us.
  useEffect(() => {
    setFilter('');
  }, [effectiveSlug, categorySlug]);

  // Handle 301 redirect from old slug to new slug.
  // Must not be gated on `categorySlug`: on the legacy `/sections/:slug` route
  // there isn't one, and skipping the redirect there left the page rendering a
  // blank heading over "No articles yet" (the 301 body carries no section).
  // Prefer the canonical category the backend resolved over the one we asked
  // with, so a renamed category doesn't just hand us another stale URL.
  useEffect(() => {
    if (!data?.redirect) return;
    const targetCategory = data.redirect.categorySlug || categorySlug;
    navigate(
      targetCategory
        ? `/${targetCategory}/${data.redirect.newSlug}`
        : `/sections/${data.redirect.newSlug}`,
      { replace: true },
    );
  }, [data, categorySlug, navigate]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    const errorCode = getErrorCode(error);
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <ErrorState
          description={
            errorCode === 'NOT_FOUND'
              ? 'This section could not be found.'
              : 'Failed to load section. Please try again.'
          }
          action={
            errorCode !== 'NOT_FOUND' && (
              <button
                onClick={() => refetch()}
                className="text-primary hover:underline"
              >
                Try again
              </button>
            )
          }
        />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { section, articles = [] } = data;

  if (!section) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <ErrorState description="This section could not be found." />
      </div>
    );
  }

  const category = section.category;
  const resolvedCategoryName = category?.name || categoryData?.category?.name;
  const canonicalSectionHref = effectiveCategorySlug
    ? `/${effectiveCategorySlug}/${section.slug}`
    : undefined;

  const isLong = articles.length > LONG_SECTION;
  const needle = fold(filter.trim());
  // Match the title and the excerpt: visitors search the words of their
  // problem, which often sit in the answer rather than the question.
  const visible = needle
    ? articles.filter((a) => fold(`${a.title} ${a.excerpt ?? ''}`).includes(needle))
    : articles;

  return (
    <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          ...(effectiveCategorySlug
            ? [{
                label: resolvedCategoryName || 'Category',
                href: `/${effectiveCategorySlug}`,
              }]
            : []),
          { label: section.name, ...(canonicalSectionHref ? { href: canonicalSectionHref } : {}) },
        ]}
      />

      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">
          {section.name}
        </h1>
        {section.description && (
          <p className="mt-2 text-muted-foreground">{section.description}</p>
        )}
      </div>

      {isLong && (
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={filterInputRef}
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={`Filter ${articles.length} articles...`}
              aria-label={`Filter articles in ${section.name}`}
              className="h-10 w-full rounded-lg border bg-background pl-10 pr-4 text-foreground placeholder:text-muted-foreground transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
          {/* Mounted even when empty: a live region that appears only once it
              has something to say is not reliably announced. */}
          <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
            {needle ? `${visible.length} of ${articles.length} articles` : ''}
          </p>
        </div>
      )}

      {articles.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No articles yet"
          description="This section doesn't have any articles yet."
        />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title="No matching articles"
          description={`Nothing in this section matches "${truncateNeedle(filter.trim())}".`}
          action={
            <button
              onClick={() => {
                setFilter('');
                // The button itself unmounts with the empty state; without this
                // a keyboard visitor is dropped back to the top of the document.
                filterInputRef.current?.focus();
              }}
              className="text-primary hover:underline"
            >
              Clear filter
            </button>
          }
        />
      ) : (
        <div className={isLong ? 'space-y-2' : 'space-y-3'}>
          {visible.map((article) => (
            <ArticleRow
              key={article.id}
              article={article}
              categorySlug={effectiveCategorySlug}
              sectionSlug={section.slug}
              compact={isLong}
            />
          ))}
        </div>
      )}
    </div>
  );
}
